// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
  toggleButtonClasses,
  toggleButtonGroupClasses,
} from '@mui/material';
import { ReactNode } from 'react';

interface Option<Value extends string | number> {
  value: Value;
  label: string;
  icon?: ReactNode;
}

const ButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  [`& .${toggleButtonClasses.root}.${toggleButtonGroupClasses.grouped}`]: {
    border: 0,
    margin: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none',
    [`& + .${toggleButtonGroupClasses.grouped}.${toggleButtonGroupClasses.selected}`]:
      {
        margin: theme.spacing(0.5),
      },
  },
}));

type ControlValue<Value, Multiple> = Multiple extends true ? Value[] : Value;

interface Props<
  Value extends string | number,
  Multiple extends boolean | undefined,
> {
  /**
   * The current value of the control.
   */
  value: ControlValue<Value, Multiple>;

  /**
   * The label of the control.
   */
  label: string;

  /**
   * The options of the control.
   */
  options: Option<Value>[];

  /**
   * If true, more than one option can be selected at a time.
   */
  multiple?: Multiple;

  /**
   * A callback fired when the value is changed.
   */
  onChange: (value: ControlValue<Value, Multiple>) => void;
}

/**
 * A controlled component for selecting a value from a list of options in a
 * "radio" style.
 */
export function SegmentedControl<
  Value extends string | number,
  Multiple extends boolean | undefined = false,
>(props: Props<Value, Multiple>) {
  const handleChange = (_: unknown, value: Value | Value[] | null) => {
    if (value === null) {
      return;
    }

    props.onChange(value as ControlValue<Value, Multiple>);
  };

  return (
    <Stack direction="row" alignSelf="end" alignItems="center" spacing={1}>
      <Typography variant="subtitle2">{props.label}</Typography>
      <ButtonGroup
        value={props.value}
        exclusive={!props.multiple}
        onChange={handleChange}
      >
        {props.options.map((option) => (
          <ToggleButton key={option.value} value={option.value}>
            {option.icon === undefined ? (
              option.label
            ) : (
              <Stack direction="row" alignItems="center" spacing={1}>
                {option.icon}
                <span>{option.label}</span>
              </Stack>
            )}
          </ToggleButton>
        ))}
      </ButtonGroup>
    </Stack>
  );
}
